import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaUser, FaEnvelope, FaPhone, FaCalendarAlt, FaMapMarkerAlt, FaGraduationCap, FaClipboardList, FaFileUpload, FaIdCard, FaUniversity, FaGraduationCap as FaCertificate } from 'react-icons/fa';
import { useAppContext } from '../../hooks/useAppContext';
import SEO from '../../components/SEO';
import api from '../../services/api';
import './Registration.css';

const Registration = () => {
  const { t } = useTranslation();
  const { language } = useAppContext();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    cinNumber: '', // Nouveau champ pour le numéro de CIN
    birthDate: '',
    address: '',
    locality: 'Kairouan', // Nouveau champ pour la localité (Kairouan par défaut)
    education: '',
    motivation: '',
    hearAboutUs: '',
    termsAccepted: false,
    // Nouveaux champs pour les pièces jointes
    cinFile: null,
    ribFile: null,
    certificateFile: null
  });
  const [formStep, setFormStep] = useState(0);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const nextStep = () => {
    // Validation des champs obligatoires selon l'étape actuelle
    if (formStep === 0) {
      // Étape 1 : Informations personnelles
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.cinNumber) {
        alert(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Veuillez remplir tous les champs obligatoires');
        return;
      }
    } else if (formStep === 1) {
      // Étape 2 : Informations supplémentaires
      // Seule la date de naissance et la localité sont obligatoires, l'adresse et le niveau d'éducation sont facultatifs
      if (!formData.birthDate || !formData.locality) {
        alert(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Veuillez remplir tous les champs obligatoires');
        return;
      }
    } else if (formStep === 2) {
      // Étape 3 : Motivations - tous les champs sont facultatifs
      // Aucune validation nécessaire, l'utilisateur peut passer à l'étape suivante
    }
    // Si tous les champs obligatoires sont remplis, passer à l'étape suivante
    setFormStep(prev => prev + 1);
  };

  const prevStep = () => {
    setFormStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Vérification finale uniquement des champs obligatoires avant soumission
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.cinNumber ||
        !formData.birthDate || !formData.locality || !formData.termsAccepted) {
      setSubmitError(language === 'ar' 
        ? 'يرجى ملء جميع الحقول المطلوبة قبل تقديم الطلب.' 
        : 'Veuillez remplir tous les champs obligatoires avant de soumettre votre demande.');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Créer un objet FormData pour envoyer les fichiers
      const formDataToSend = new FormData();
      
      // Ajouter les champs texte
      Object.keys(formData).forEach(key => {
        if (key !== 'cinFile' && key !== 'ribFile' && key !== 'certificateFile') {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Ajouter les fichiers s'ils existent
      if (formData.cinFile) formDataToSend.append('cinFile', formData.cinFile);
      if (formData.ribFile) formDataToSend.append('ribFile', formData.ribFile);
      if (formData.certificateFile) formDataToSend.append('certificateFile', formData.certificateFile);
      
      // Envoyer les données au backend via l'API configurée
      const response = await api.createRegistration(formDataToSend);
      console.log('Inscription réussie:', response.data);
      setFormSubmitted(true);
    } catch (error) {
      console.error('Erreur lors de la soumission du formulaire:', error);
      setSubmitError(language === 'ar' ? 'حدث خطأ أثناء تقديم النموذج. يرجى المحاولة مرة أخرى.' : 'Une erreur est survenue lors de la soumission du formulaire. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.2, 
        duration: 0.5 
      } 
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1 
    }
  };

  const renderFormStep = () => {
    switch (formStep) {
      case 0:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  {language === 'ar' ? 'الاسم الأول' : 'Prénom'}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="text-gray-500" />
                  </div>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    required
                    placeholder={language === 'ar' ? 'أدخل اسمك الأول' : 'Entrez votre prénom'}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  {language === 'ar' ? 'اللقب' : 'Nom'}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="text-gray-500" />
                  </div>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    required
                    placeholder={language === 'ar' ? 'أدخل لقبك' : 'Entrez votre nom'}
                  />
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                {language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaEnvelope className="text-gray-500" />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  required
                  placeholder={language === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Entrez votre email'}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                {language === 'ar' ? 'رقم الهاتف' : 'Téléphone'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaPhone className="text-gray-500" />
                </div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  required
                  placeholder={language === 'ar' ? 'أدخل رقم هاتفك' : 'Entrez votre numéro de téléphone'}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                {language === 'ar' ? 'رقم بطاقة التعريف الوطنية' : 'Numéro de CIN'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaIdCard className="text-gray-500" />
                </div>
                <input
                  type="text"
                  name="cinNumber"
                  value={formData.cinNumber}
                  onChange={handleChange}
                  className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  required
                  placeholder={language === 'ar' ? 'أدخل رقم بطاقة التعريف الوطنية' : 'Entrez votre numéro de CIN'}
                />
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                {language === 'ar' ? 'تاريخ الميلاد' : 'Date de naissance'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaCalendarAlt className="text-gray-500" />
                </div>
                <input
                  type="date"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleChange}
                  className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                {language === 'ar' ? 'العنوان' : 'Adresse'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaMapMarkerAlt className="text-gray-500" />
                </div>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  placeholder={language === 'ar' ? ' (اختياري) أدخل عنوانك' : 'Entrez votre adresse (facultatif)'}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                {language === 'ar' ? 'المنطقة' : 'Localité'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaMapMarkerAlt className="text-gray-500" />
                </div>
                <select
                  name="locality"
                  value={formData.locality}
                  onChange={handleChange}
                  className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  required
                >
                  <option value="Kairouan">{language === 'ar' ? 'القيروان' : 'Kairouan'}</option>
                  <option value="Sousse">{language === 'ar' ? 'سوسة' : 'Sousse'}</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                {language === 'ar' ? '(اختياري) المستوى التعليمي' : 'Niveau d\'éducation (facultatif)'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaGraduationCap className="text-gray-500" />
                </div>
                <select
                  name="education"
                  value={formData.education}
                  onChange={handleChange}
                  className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                 
                >
                  <option value="">{language === 'ar' ? 'اختر خيارًا' : 'Sélectionnez une option'}</option>
                  <option value="6ème">{language === 'ar' ? 'السنة السادسة' : '6ème année'}</option>
                  <option value="7ème">{language === 'ar' ? 'السنة السابعة' : '7ème année'}</option>
                  <option value="8ème">{language === 'ar' ? 'السنة الثامنة' : '8ème année'}</option>
                  <option value="9ème">{language === 'ar' ? 'السنة التاسعة' : '9ème année'}</option>
                  <option value="1ère">{language === 'ar' ? 'السنة الأولى ثانوي' : '1ère année secondaire'}</option>
                  <option value="2ème">{language === 'ar' ? 'السنة الثانية ثانوي' : '2ème année secondaire'}</option>
                  <option value="3ème">{language === 'ar' ? 'السنة الثالثة ثانوي' : '3ème année secondaire'}</option>
                  <option value="Bac">{language === 'ar' ? 'باكالوريا' : 'Baccalauréat'}</option>
                  <option value="CAP">{language === 'ar' ? 'شهادة الكفاءة المهنية (CAP)' : 'Formation professionnelle (CAP)'}</option>
                  <option value="BTP">{language === 'ar' ? 'شهادة البناء والأشغال العامة (BTP)' : 'Formation professionnelle (BTP)'}</option>
                  <option value="BTS">{language === 'ar' ? 'شهادة التقني العالي (BTS)' : 'Formation professionnelle (BTS)'}</option>
                  <option value="Licence">{language === 'ar' ? 'إجازة' : 'Licence'}</option>
                  <option value="Autre">{language === 'ar' ? 'أخرى' : 'Autre'}</option>
                </select>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                {language === 'ar' ? 'ما الذي يحفزك للانضمام إلى المنظومة الوطنية للفرصة الجديدة؟ (اختياري)' : 'Qu\'est-ce qui vous motive à rejoindre le Dispositif National Nouvelle Chance ? (facultatif)'}
              </label>
              <div className="relative">
                <div className="absolute top-3 left-0 pl-3 flex items-start pointer-events-none">
                  <FaClipboardList className="text-gray-500" />
                </div>
                <textarea
                  name="motivation"
                  value={formData.motivation}
                  onChange={handleChange}
                  rows="4"
                  className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  placeholder={language === 'ar' ? 'اشرح دوافعك...' : 'Expliquez vos motivations...'}
                ></textarea>
              </div>
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                {language === 'ar' ? 'كيف سمعت عنا؟ (اختياري)' : 'Comment avez-vous entendu parler de nous ? (facultatif)'}
              </label>
              <select
                name="hearAboutUs"
                value={formData.hearAboutUs}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
              >
                <option value="">{language === 'ar' ? 'اختر خيارًا' : 'Sélectionnez une option'}</option>
                <option value="socialMedia">{language === 'ar' ? 'وسائل التواصل الاجتماعي' : 'Réseaux sociaux'}</option>
                <option value="friend">{language === 'ar' ? 'صديق أو قريب' : 'Ami ou parent'}</option>
                <option value="event">{language === 'ar' ? 'حدث أو ورشة عمل' : 'Événement ou atelier'}</option>
                <option value="school">{language === 'ar' ? 'المدرسة أو الجامعة' : 'École ou université'}</option>
                <option value="newspaper">{language === 'ar' ? 'صحيفة أو مجلة' : 'Journal ou magazine'}</option>
                <option value="other">{language === 'ar' ? 'مصدر آخر' : 'Autre source'}</option>
              </select>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">
              {language === 'ar' ? 'تحميل المستندات (اختياري)' : 'Téléchargement de documents (facultatif)'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {language === 'ar' 
                ? 'هذه المستندات اختيارية في هذه المرحلة. يمكنك تقديمها لاحقًا إذا تم قبولك في المنظومة.' 
                : 'Ces documents sont facultatifs à ce stade. Vous pourrez les fournir ultérieurement si vous êtes accepté dans le programme.'}
            </p>
            
            <div className="file-upload-container">
              <label className="block text-gray-700 font-medium mb-2">
                <div className="flex items-center">
                  <FaIdCard className="mr-2 text-gray-600" />
                  {language === 'ar' ? 'بطاقة الهوية الوطنية (CIN)' : 'Carte d\'identité nationale (CIN)'}
                </div>
              </label>
              <div className="file-input-wrapper">
                <input
                  type="file"
                  name="cinFile"
                  onChange={handleChange}
                  className="file-input"
                  accept=".pdf,.jpg,.jpeg,.png"
                  // Pas de required ici car c'est facultatif
                />
                <div className="file-input-button">
                  <FaFileUpload className="mr-2" />
                  {language === 'ar' ? 'اختر ملفًا' : 'Choisir un fichier'}
                </div>
                <span className="file-name">
                  {formData.cinFile ? formData.cinFile.name : language === 'ar' ? 'لم يتم اختيار ملف' : 'Aucun fichier choisi'}
                </span>
              </div>
            </div>
            
            <div className="file-upload-container">
              <label className="block text-gray-700 font-medium mb-2">
                <div className="flex items-center">
                  <FaUniversity className="mr-2 text-gray-600" />
                  {language === 'ar' ? 'الهوية البنكية (RIB)' : 'Relevé d\'identité bancaire (RIB)'}
                </div>
              </label>
              <div className="file-input-wrapper">
                <input
                  type="file"
                  name="ribFile"
                  onChange={handleChange}
                  className="file-input"
                  accept=".pdf,.jpg,.jpeg,.png"
                  // Pas de required ici car c'est facultatif
                />
                <div className="file-input-button">
                  <FaFileUpload className="mr-2" />
                  {language === 'ar' ? 'اختر ملفًا' : 'Choisir un fichier'}
                </div>
                <span className="file-name">
                  {formData.ribFile ? formData.ribFile.name : language === 'ar' ? 'لم يتم اختيار ملف' : 'Aucun fichier choisi'}
                </span>
              </div>
            </div>
            
            <div className="file-upload-container">
              <label className="block text-gray-700 font-medium mb-2">
                <div className="flex items-center">
                  <FaCertificate className="mr-2 text-gray-600" />
                  {language === 'ar' ? 'الشهادة المدرسية' : 'Certificat scolaire'}
                </div>
              </label>
              <div className="file-input-wrapper">
                <input
                  type="file"
                  name="certificateFile"
                  onChange={handleChange}
                  className="file-input"
                  accept=".pdf,.jpg,.jpeg,.png"
                  // Pas de required ici car c'est facultatif
                />
                <div className="file-input-button">
                  <FaFileUpload className="mr-2" />
                  {language === 'ar' ? 'اختر ملفًا' : 'Choisir un fichier'}
                </div>
                <span className="file-name">
                  {formData.certificateFile ? formData.certificateFile.name : language === 'ar' ? 'لم يتم اختيار ملف' : 'Aucun fichier choisi'}
                </span>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>{language === 'ar' ? 'تذكير:' : 'Rappel :'}</strong> {language === 'ar' 
                  ? 'تحميل المستندات اختياري في هذه المرحلة، لكن جميع الحقول الأخرى في النموذج إلزامية.' 
                  : 'Le téléchargement des documents est facultatif à ce stade, mais tous les autres champs du formulaire sont obligatoires.'}
              </p>
            </div>
          </div>
        );
      default:
        return (
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                name="termsAccepted"
                checked={formData.termsAccepted}
                onChange={handleChange}
                className="h-5 w-5 text-primary focus:ring-primary border-gray-300 rounded"
                required
              />
              <label className="ml-2 block text-gray-700">
                {language === 'ar' 
                  ? 'أوافق على شروط وأحكام المنظومة الوطنية للفرصة الجديدة وسياسة الخصوصية.' 
                  : 'J\'accepte les conditions générales du Dispositif National Nouvelle Chance et la politique de confidentialité.'}
              </label>
            </div>
          </div>
        );
    }
  };

  const getStepTitle = () => {
    switch (formStep) {
      case 0:
        return language === 'ar' ? 'المعلومات الشخصية' : 'Informations personnelles';
      case 1:
        return language === 'ar' ? 'معلومات إضافية' : 'Informations supplémentaires';
      case 2:
        return language === 'ar' ? 'الدوافع والاهتمامات' : 'Motivations et intérêts';
      case 3:
        return language === 'ar' ? 'المستندات' : 'Documents';
      default:
        return language === 'ar' ? 'تأكيد' : 'Confirmation';
    }
  };

  if (formSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.div 
            className="bg-white shadow-xl rounded-lg p-5 mt-5 sm:mt-8 lg:mt-10 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="success-icon mb-6">
              <svg className="mx-auto h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              {language === 'ar' ? 'تم تقديم طلبك بنجاح!' : 'Votre demande a été soumise avec succès !'}
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              {language === 'ar' 
                ? 'شكرًا لاهتمامك بالمنظومة الوطنية للفرصة الجديدة. سنراجع طلبك ونتواصل معك قريبًا.' 
                : 'Merci pour votre intérêt pour le Dispositif National Nouvelle Chance. Nous examinerons votre demande et vous contacterons bientôt.'}
            </p>
            <motion.button
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.href = '/'}
            >
              {language === 'ar' ? 'العودة إلى الصفحة الرئيسية' : 'Retour à la page d\'accueil'}
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 mt-6 sm:px-6 sm:mt-6 lg:px-8 lg:mt-8">
      <SEO 
        title={language === 'ar' ? 'التسجيل - DNC تونس' : 'Inscription - Dispositif National Nouvelle Chance'}
        description={language === 'ar' ? 'سجل الآن في المنظومة الوطنية للفرصة الجديدة في سوسة والقيروان' : 'Inscrivez-vous maintenant au Dispositif National Nouvelle Chance à Sousse et Kairouan'}
        url="/registration"
        type="website"
      />
      <div className="max-w-3xl mx-auto">
        <motion.div 
          className="text-center mb-10"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold text-gray-900 mt-10 mb-4">
            {language === 'ar' ? 'التسجيل في المنظومة الوطنية للفرصة الجديدة' : 'Inscription au Dispositif National Nouvelle Chance'}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'ar' 
              ? 'املأ النموذج أدناه للتسجيل في منظومتنا. سنراجع طلبك ونتواصل معك في أقرب وقت ممكن.' 
              : 'Remplissez le formulaire ci-dessous pour vous inscrire à notre programme. Nous examinerons votre demande et vous contacterons dès que possible.'}
          </p>
        </motion.div>
        
        <motion.div 
          className="bg-white shadow-xl rounded-lg overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="progress-bar-container">
            <div className="progress-steps">
              {[0, 1, 2, 3, 4].map((step) => (
                <div 
                  key={step} 
                  className={`progress-step ${formStep >= step ? 'active' : ''}`}
                  onClick={() => formStep > step && setFormStep(step)}
                >
                  <div className="step-number">{step + 1}</div>
                  <div className="step-label">{step === 0 
                    ? (language === 'ar' ? 'معلومات شخصية' : 'Infos personnelles')
                    : step === 1 
                    ? (language === 'ar' ? 'معلومات إضافية' : 'Infos supplémentaires')
                    : step === 2
                    ? (language === 'ar' ? 'الدوافع' : 'Motivations')
                    : step === 3
                    ? (language === 'ar' ? 'المستندات' : 'Documents')
                    : (language === 'ar' ? 'تأكيد' : 'Confirmation')
                  }</div>
                </div>
              ))}
            </div>
            <div 
              className="progress-bar" 
              style={{ width: `${(formStep / 4) * 100}%` }}
            ></div>
          </div>
          
          <div className="p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              {getStepTitle()}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {renderFormStep()}
                
                <div className="mt-8 flex justify-between">
                  {formStep > 0 && (
                    <motion.button
                      type="button"
                      onClick={prevStep}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      variants={itemVariants}
                    >
                      {language === 'ar' ? 'السابق' : 'Précédent'}
                    </motion.button>
                  )}
                  
                  {formStep < 4 ? (
                    <motion.button
                      type="button"
                      onClick={nextStep}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ml-auto"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      variants={itemVariants}
                    >
                      {language === 'ar' ? 'التالي' : 'Suivant'}
                    </motion.button>
                  ) : (
                    <motion.button
                      type="submit"
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ml-auto"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      variants={itemVariants}
                      disabled={!formData.termsAccepted || isSubmitting}
                    >
                      {isSubmitting ? (language === 'ar' ? 'جاري الإرسال...' : 'Envoi en cours...') : (language === 'ar' ? 'إرسال الطلب' : 'Soumettre la demande')}
                    </motion.button>
                  )}
                </div>
              </motion.div>
            </form>
            {submitError && (
              <div className="mt-4 text-red-500 text-sm">
                {submitError}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Registration;
